/**
 * OnboardingFlow.jsx - Immersive onboarding experience
 *
 * Steps:
 *  0  Welcome       — Cinematic intro with tier badge + animated logo
 *  1  Your Profile  — New / Recreational / Sharp profile cards
 *  2  My Books      — Sportsbook selection
 *  3  Preferences   — Unit size + EV threshold
 *  4  Sports        — Favorite sports
 *  5  Ready         — Summary + launch
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Platform,
  ScrollView, Image, TextInput, Animated, Dimensions,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import { GlassBackgroundGradient, GradientOverlay } from './Gradients';
import { getSportsbookLogo } from '../data/logos';
import { SPORTSBOOK_TIERS } from '../constants/docsData';

const { width: W, height: H } = Dimensions.get('window');

// ─── Tier 1 + 2 books for onboarding selection ────────────────────────────────
// Derived from SPORTSBOOK_TIERS at module load — ordered most → least popular

const _t1 = SPORTSBOOK_TIERS?.find(t => t.tier === 1);
const _t2 = SPORTSBOOK_TIERS?.find(t => t.tier === 2);

// Explicit popularity order for Tier 1 (most recognizable US books first)
const T1_POPULARITY = [
  'draftkings','fanduel','betmgm','caesars','espn_bet','fanatics','betrivers',
  'hard_rock','pointsbet','wynnbet','bet365','pinnacle','unibet','betway',
  'betonline','bovada','mybookie','superbook','tipico','betfred','circa_sports',
  'novig','fliff','underdog','barstool','fox_bet','bally_bet','borgata',
  'betparx','si','thescore','golden_nugget','betus','betr','circa_vegas',
  'desert_diamond','four_winds','play_eagle',
];

const ONBOARDING_BOOKS_T1 = (_t1?.books || [])
  .map(b => ({ id: b.id, name: b.label, tier: 1 }))
  .sort((a, b) => {
    const ai = T1_POPULARITY.indexOf(a.id);
    const bi = T1_POPULARITY.indexOf(b.id);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

const ONBOARDING_BOOKS_T2 = (_t2?.books || [])
  .map(b => ({ id: b.id, name: b.label, tier: 2 }));

// ─── ProfileCard — defined at module level to preserve hook stability ─────────

function ProfileCard({ profile, anim, selected, onPress }) {
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const glowAnim  = useRef(new Animated.Value(0)).current;

  const handleIn = () => {
    Animated.parallel([
      Animated.timing(scaleAnim, { toValue: 1.03, duration: 220, useNativeDriver: true }),
      Animated.timing(glowAnim,  { toValue: 1,    duration: 220, useNativeDriver: false }),
    ]).start();
  };
  const handleOut = () => {
    Animated.parallel([
      Animated.timing(scaleAnim, { toValue: 1,    duration: 260, useNativeDriver: true }),
      Animated.timing(glowAnim,  { toValue: 0,    duration: 260, useNativeDriver: false }),
    ]).start();
  };

  const rotateInterp = anim.rotate.interpolate({
    inputRange: [-10, 0, 10],
    outputRange: ['-10deg', '0deg', '10deg'],
  });

  return (
    <Animated.View style={[
      styles.profileCardWrap,
      { opacity: anim.opacity, transform: [{ translateY: anim.y }, { rotate: rotateInterp }, { scale: scaleAnim }] },
    ]}>
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.9}
        {...(Platform.OS === 'web' ? { onMouseEnter: handleIn, onMouseLeave: handleOut } : {})}
        style={{ flex: 1 }}
      >
        {/* Glass card — Plans page style */}
        <View style={[
          styles.profileCard,
          selected && { borderTopColor: `${profile.accent}90`, borderLeftColor: `${profile.accent}60`, borderRightColor: `${profile.accent}40`, borderBottomColor: `${profile.accent}20` },
        ]}>
          <GlassBackgroundGradient borderRadius={20} />
          <GradientOverlay opacity={glowAnim} borderRadius={20} />
          <BlurView intensity={40} tint="dark" style={styles.profileCardBlur}>
            {/* Selected accent bar */}
            {selected && (
              <LinearGradient
                colors={[profile.accent, `${profile.accent}00`]}
                style={styles.profileAccentBar}
                start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
              />
            )}
            <View style={[styles.profileIconCircle, selected && { borderColor: `${profile.accent}50`, backgroundColor: `${profile.accent}15` }]}>
              <Ionicons name={profile.icon} size={24} color={selected ? profile.accent : 'rgba(255,255,255,0.5)'} />
            </View>
            <Text style={[styles.profileLabel, selected && { color: '#fff' }]}>{profile.label}</Text>
            <Text style={styles.profileTagline}>{profile.tagline}</Text>
            <View style={[styles.profileTag, selected && { backgroundColor: `${profile.accent}18`, borderColor: `${profile.accent}45` }]}>
              <Text style={[styles.profileTagText, selected && { color: profile.accent }]}>{profile.tag}</Text>
            </View>
          </BlurView>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
}

// ─── Static data ──────────────────────────────────────────────────────────────

const DEFAULT_WELCOME_FEATURES = [
  { icon: 'analytics-outline',  title: 'Smart EV Analysis',       desc: 'Find +EV bets across 300+ sportsbooks' },
  { icon: 'flash-outline',      title: 'Natural Language Search',  desc: 'Search like "LeBron points over 25"' },
  { icon: 'calculator-outline', title: 'Kelly Criterion',          desc: 'Optimized bet sizing for your bankroll' },
];

const DEFAULT_SPORTSBOOKS = [
  { id: 'fanduel',    name: 'FanDuel',       region: 'US' },
  { id: 'draftkings', name: 'DraftKings',    region: 'US' },
  { id: 'betmgm',     name: 'BetMGM',        region: 'US' },
  { id: 'caesars',    name: 'Caesars',        region: 'US' },
  { id: 'espn_bet',   name: 'ESPN BET',       region: 'US' },
  { id: 'fanatics',   name: 'Fanatics',       region: 'US' },
  { id: 'bet365',     name: 'Bet365',         region: 'US/INT' },
  { id: 'betrivers',  name: 'BetRivers',      region: 'US' },
  { id: 'hard_rock',  name: 'Hard Rock Bet',  region: 'US' },
  { id: 'pointsbet',  name: 'PointsBet',      region: 'US' },
  { id: 'wynnbet',    name: 'WynnBET',        region: 'US' },
  { id: 'pinnacle',   name: 'Pinnacle',       region: 'INT' },
  { id: 'betway',     name: 'Betway',         region: 'INT' },
  { id: 'unibet',     name: 'Unibet',         region: 'INT' },
  { id: 'bovada',     name: 'Bovada',         region: 'INT' },
  { id: 'betonline',  name: 'BetOnline',      region: 'INT' },
  { id: 'mybookie',   name: 'MyBookie',       region: 'INT' },
  { id: 'superbook',  name: 'SuperBook',      region: 'US' },
  { id: 'tipico',     name: 'Tipico',         region: 'US' },
  { id: 'betfred',    name: 'Betfred',        region: 'US' },
  { id: 'circa',      name: 'Circa Sports',   region: 'US' },
  { id: 'fliff',      name: 'Fliff',          region: 'US' },
  { id: 'underdog',   name: 'Underdog',       region: 'US' },
  { id: 'sleeper',    name: 'Sleeper',        region: 'US' },
];
const POPULAR_SPORTSBOOKS = DEFAULT_SPORTSBOOKS;

const DEFAULT_SPORTS_OPTIONS = [
  { id: 'nba',    name: 'NBA',     icon: '🏀', league: 'Basketball' },
  { id: 'nfl',    name: 'NFL',     icon: '🏈', league: 'Football' },
  { id: 'mlb',    name: 'MLB',     icon: '⚾', league: 'Baseball' },
  { id: 'nhl',    name: 'NHL',     icon: '🏒', league: 'Hockey' },
  { id: 'ncaab',  name: 'NCAAB',   icon: '🏀', league: 'College Basketball' },
  { id: 'ncaaf',  name: 'NCAAF',   icon: '🏈', league: 'College Football' },
  { id: 'soccer', name: 'Soccer',  icon: '⚽', league: 'Soccer' },
  { id: 'tennis', name: 'Tennis',  icon: '🎾', league: 'Tennis' },
  { id: 'ufc',    name: 'UFC/MMA', icon: '🥊', league: 'MMA' },
  { id: 'golf',   name: 'Golf',    icon: '⛳', league: 'Golf' },
];
const SPORTS_OPTIONS = DEFAULT_SPORTS_OPTIONS;

const DEFAULT_UNIT_SIZES = ['10', '25', '50', '100', '250'];

const BETTING_PROFILES = [
  {
    id: 'new_bettor',
    label: 'New to Betting',
    tagline: "Just getting started",
    desc: "We'll guide you step-by-step, explain the math, and highlight safe +EV spots perfect for beginners.",
    icon: 'leaf-outline',
    gradient: ['#1e3a5f', '#0f2540'],
    accent: '#60a5fa',
    tag: 'BEGINNER FRIENDLY',
    betExperience: 'beginner',
  },
  {
    id: 'recreational',
    label: 'Recreational',
    tagline: 'I bet occasionally for fun',
    desc: "You know the basics. We'll surface the best value without the noise, optimized for your schedule.",
    icon: 'trophy-outline',
    gradient: ['#3b1f6b', '#1e0f40'],
    accent: '#a78bfa',
    tag: 'MOST POPULAR',
    betExperience: 'intermediate',
  },
  {
    id: 'sharp',
    label: 'Sharp / Pro',
    tagline: 'I live and breathe edges',
    desc: "Full EV data, line movement, CLV tracking, and every sportsbook we cover. No hand-holding needed.",
    icon: 'flash-outline',
    gradient: ['#1a3a2a', '#0a1f15'],
    accent: '#4ade80',
    tag: 'FULL ACCESS',
    betExperience: 'advanced',
  },
];

const TIER_CONFIG = {
  alpha: {
    badge: 'ALPHA ACCESS',
    badgeBg: 'rgba(251,191,36,0.15)',
    badgeBorder: 'rgba(251,191,36,0.5)',
    badgeText: '#fbbf24',
    headline: 'Welcome to the Alpha',
    sub: "You're among the first to experience Bet Optimized. Your feedback shapes the platform.",
    glowColor: 'rgba(251,191,36,0.2)',
  },
  beta: {
    badge: 'BETA TESTER',
    badgeBg: 'rgba(139,92,246,0.2)',
    badgeBorder: 'rgba(139,92,246,0.6)',
    badgeText: '#a78bfa',
    headline: 'Welcome to the Beta',
    sub: "You have early access to Bet Optimized. Expect rapid improvements and new features weekly.",
    glowColor: 'rgba(139,92,246,0.25)',
  },
  standard: {
    badge: null,
    badgeBg: null,
    badgeBorder: null,
    badgeText: null,
    headline: 'Welcome to Bet Optimized',
    sub: 'Your intelligent sports betting companion. Built to find edges, not just picks.',
    glowColor: 'rgba(139,92,246,0.2)',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const OnboardingFlow = ({
  onComplete,
  onSkip,
  isPreview    = false,
  initialStep  = 0,
  config       = null,
  onStepChange = null,
  userTier     = 'standard', // 'alpha' | 'beta' | 'standard'
}) => {

  const tierCfg = TIER_CONFIG[userTier] || TIER_CONFIG.standard;

  // Config-driven overrides
  const activeBooks = useMemo(() => {
    if (!config?.books || !Array.isArray(config.books)) return DEFAULT_SPORTSBOOKS;
    const enabled = new Set(config.books.filter(b => b.enabled !== false).map(b => b.id));
    return DEFAULT_SPORTSBOOKS.filter(b => enabled.has(b.id));
  }, [config]);

  const activeSports = useMemo(() => {
    if (!config?.sports || !Array.isArray(config.sports)) return DEFAULT_SPORTS_OPTIONS;
    const enabled = new Set(config.sports.filter(s => s.enabled !== false).map(s => s.id));
    return DEFAULT_SPORTS_OPTIONS.filter(s => enabled.has(s.id));
  }, [config]);

  const activeUnitSizes = useMemo(() => {
    if (config?.unitSizeOptions?.length) return config.unitSizeOptions.map(String);
    return DEFAULT_UNIT_SIZES;
  }, [config]);

  const welcomeFeatures = useMemo(() => {
    if (config?.welcomeFeatures?.length) return config.welcomeFeatures;
    return DEFAULT_WELCOME_FEATURES;
  }, [config]);

  // User state
  const [currentStep,    setCurrentStep]    = useState(initialStep);
  const [bettingProfile, setBettingProfile] = useState(null); // BETTING_PROFILES id
  const [selectedBooks,  setSelectedBooks]  = useState([]);
  const [unitSize,       setUnitSize]       = useState('10');
  const [evThreshold,    setEvThreshold]    = useState('0');
  const [selectedSports, setSelectedSports] = useState(['nba', 'nfl']);

  const totalSteps = 6;

  // Animations
  const fadeAnim  = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const logoScale = useRef(new Animated.Value(1)).current;
  const logoPulse = useRef(null);

  // Per-card splay animations for the profile step
  const splayAnims = useRef(
    BETTING_PROFILES.map(() => ({
      y:       new Animated.Value(60),
      opacity: new Animated.Value(0),
      rotate:  new Animated.Value(0),
    }))
  ).current;

  useEffect(() => {
    if (currentStep === 0) {
      logoPulse.current = Animated.loop(
        Animated.sequence([
          Animated.timing(logoScale, { toValue: 1.05, duration: 2200, useNativeDriver: true }),
          Animated.timing(logoScale, { toValue: 1.00, duration: 2200, useNativeDriver: true }),
        ])
      );
      logoPulse.current.start();
      return () => { logoPulse.current?.stop(); };
    }

    if (currentStep === 1) {
      // Reset each card to starting position (slight splay)
      const startRotations = [-5, 0, 5];
      splayAnims.forEach((a, i) => {
        a.y.setValue(60);
        a.opacity.setValue(0);
        a.rotate.setValue(startRotations[i]);
      });
      // Stagger spring into final resting position
      Animated.stagger(90, splayAnims.map(a =>
        Animated.parallel([
          Animated.spring(a.y,       { toValue: 0,  tension: 70, friction: 10, useNativeDriver: true }),
          Animated.spring(a.rotate,  { toValue: 0,  tension: 70, friction: 10, useNativeDriver: true }),
          Animated.timing(a.opacity, { toValue: 1,  duration: 280, useNativeDriver: true }),
        ])
      )).start();
    }
  }, [currentStep]);

  const animateTransition = (callback) => {
    Animated.parallel([
      Animated.timing(fadeAnim,  { toValue: 0, duration: 180, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: -24, duration: 180, useNativeDriver: true }),
    ]).start(() => {
      callback();
      slideAnim.setValue(24);
      Animated.parallel([
        Animated.timing(fadeAnim,  { toValue: 1, duration: 260, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 260, useNativeDriver: true }),
      ]).start();
    });
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      const next = currentStep + 1;
      animateTransition(() => {
        setCurrentStep(next);
        if (onStepChange) onStepChange(next);
      });
    } else {
      handleComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      animateTransition(() => {
        setCurrentStep(prev);
        if (onStepChange) onStepChange(prev);
      });
    }
  };

  const handleComplete = () => {
    const profile = BETTING_PROFILES.find(p => p.id === bettingProfile);
    const prefs = {
      bettingProfile,
      myBooks:       selectedBooks,
      unitSize:      parseFloat(unitSize) || 10,
      evThreshold:   parseFloat(evThreshold) || 0,
      selectedSports,
      betExperience: profile?.betExperience || 'intermediate',
      onboardingCompleted:   true,
      onboardingCompletedAt: new Date().toISOString(),
    };
    if (onComplete) onComplete(prefs);
  };

  const toggleBook  = (id) => setSelectedBooks(prev  => prev.includes(id)  ? prev.filter(b => b !== id)  : [...prev, id]);
  const toggleSport = (id) => setSelectedSports(prev => prev.includes(id)  ? prev.filter(s => s !== id) : [...prev, id]);

  // ── Progress bar ─────────────────────────────────────────────────────────────
  const renderProgress = () => (
    <View style={styles.progressWrap}>
      {isPreview && (
        <View style={styles.previewBadge}>
          <Text style={styles.previewBadgeText}>PREVIEW MODE</Text>
        </View>
      )}
      <View style={styles.progressTrack}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <View key={i} style={[
            styles.progressSeg,
            i < currentStep  && styles.progressSegDone,
            i === currentStep && styles.progressSegActive,
          ]} />
        ))}
      </View>
      <Text style={styles.progressLabel}>{currentStep + 1} of {totalSteps}</Text>
    </View>
  );

  // ── Step 0: Welcome ──────────────────────────────────────────────────────────
  const renderWelcome = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.welcomeScroll} showsVerticalScrollIndicator={false}>
      {/* Tier badge */}
      {tierCfg.badge && (
        <View style={[styles.tierBadge, { backgroundColor: tierCfg.badgeBg, borderColor: tierCfg.badgeBorder }]}>
          <View style={[styles.tierBadgeDot, { backgroundColor: tierCfg.badgeText }]} />
          <Text style={[styles.tierBadgeText, { color: tierCfg.badgeText }]}>{tierCfg.badge}</Text>
        </View>
      )}

      {/* Animated logo */}
      <Animated.View style={[styles.logoWrap, { transform: [{ scale: logoScale }] }]}>
        <View style={[styles.logoGlow, { backgroundColor: tierCfg.glowColor }]} />
        <LinearGradient colors={['#8B5CF6', '#6366F1', '#4f46e5']} style={styles.logoGradient} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}>
          <Text style={styles.logoText}>b°</Text>
        </LinearGradient>
      </Animated.View>

      <Text style={styles.welcomeHeadline}>{tierCfg.headline}</Text>
      <Text style={styles.welcomeSub}>{tierCfg.sub}</Text>

      {/* Stat pills */}
      <View style={styles.statPills}>
        {[
          { value: '300+', label: 'Sportsbooks',    icon: 'business-outline',     color: '#a78bfa' },
          { value: '+EV',  label: 'Verified Edges', icon: 'trending-up-outline',  color: '#4ade80' },
          { value: 'Live', label: 'Odds Updates',   icon: 'flash-outline',        color: '#60a5fa' },
        ].map((s, i) => (
          <View key={i} style={styles.statPill}>
            <GlassBackgroundGradient borderRadius={12} />
            <Ionicons name={s.icon} size={15} color={s.color} />
            <Text style={[styles.statPillValue, { color: s.color }]}>{s.value}</Text>
            <Text style={styles.statPillLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      {/* Feature cards */}
      <View style={styles.featureGrid}>
        {welcomeFeatures.map((feat, i) => (
          <View key={i} style={styles.featureCard}>
            <GlassBackgroundGradient borderRadius={14} />
            <BlurView intensity={30} tint="dark" style={styles.featureCardBlur}>
              <View style={styles.featureCardIcon}>
                <Ionicons name={feat.icon || 'star-outline'} size={20} color="#a78bfa" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.featureCardTitle}>{feat.title}</Text>
                <Text style={styles.featureCardDesc}>{feat.desc}</Text>
              </View>
            </BlurView>
          </View>
        ))}
      </View>
    </ScrollView>
  );

  // ── Step 1: Profile selection ─────────────────────────────────────────────────
  const renderProfile = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepTitle}>How do you bet?</Text>
      <Text style={styles.stepSub}>We'll personalize your experience based on your style.</Text>

      <View style={styles.profileRow}>
        {BETTING_PROFILES.map((profile, index) => (
          <ProfileCard
            key={profile.id}
            profile={profile}
            anim={splayAnims[index]}
            selected={bettingProfile === profile.id}
            onPress={() => setBettingProfile(profile.id)}
          />
        ))}
      </View>

      {/* Selected description panel */}
      {bettingProfile && (() => {
        const p = BETTING_PROFILES.find(x => x.id === bettingProfile);
        return p ? (
          <View style={[styles.profileDescPanel, { borderColor: `${p.accent}30`, backgroundColor: `${p.accent}0a` }]}>
            <Ionicons name={p.icon} size={16} color={p.accent} style={{ marginRight: 8 }} />
            <Text style={[styles.profileDescText, { color: 'rgba(255,255,255,0.7)' }]}>{p.desc}</Text>
          </View>
        ) : null;
      })()}
    </View>
  );

  // ── Step 2: My Books ──────────────────────────────────────────────────────────
  const renderBookGrid = (books) => (
    <View style={styles.booksGrid}>
      {books.map(book => {
        const sel = selectedBooks.includes(book.id);
        const logo = getSportsbookLogo(book.id);
        return (
          <TouchableOpacity
            key={book.id}
            onPress={() => toggleBook(book.id)}
            activeOpacity={0.8}
          >
            <View style={[styles.bookCard, sel && styles.bookCardSel]}>
              <GlassBackgroundGradient borderRadius={14} />
              {sel && (
                <View style={styles.bookCheck}>
                  <Ionicons name="checkmark-circle" size={16} color="#10B981" />
                </View>
              )}
              <View style={styles.bookLogoWrap}>
                {logo
                  ? <Image source={{ uri: logo }} style={styles.bookLogo} resizeMode="contain" />
                  : <Text style={styles.bookLogoFallbackText}>{book.name.charAt(0)}</Text>}
              </View>
              <Text style={[styles.bookName, sel && { color: '#fff' }]} numberOfLines={2}>{book.name}</Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const renderMyBooks = () => (
    <View style={styles.stepContent}>
      <View style={styles.stepHeader}>
        <LinearGradient colors={['rgba(139,92,246,0.2)', 'rgba(99,102,241,0.1)']} style={styles.stepIconCircle}>
          <Ionicons name="bookmark" size={26} color="#a78bfa" />
        </LinearGradient>
        <Text style={styles.stepTitle}>Your Sportsbooks</Text>
        <Text style={styles.stepSub}>Select where you have accounts. Results will default to your books.</Text>
      </View>

      <View style={styles.bookCountRow}>
        <Text style={styles.bookCountText}>
          {selectedBooks.length > 0 ? `${selectedBooks.length} selected` : 'None selected'}
        </Text>
        {selectedBooks.length > 0 && (
          <TouchableOpacity onPress={() => setSelectedBooks([])}>
            <Text style={styles.bookClearText}>Clear all</Text>
          </TouchableOpacity>
        )}
      </View>

      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 24 }}>
        {/* Tier 1 */}
        <View style={styles.tierSectionHeader}>
          <View style={[styles.tierDot, { backgroundColor: '#fbbf24' }]} />
          <Text style={styles.tierSectionLabel}>Major US Books</Text>
          <Text style={styles.tierSectionCount}>{ONBOARDING_BOOKS_T1.length} books</Text>
        </View>
        {renderBookGrid(ONBOARDING_BOOKS_T1)}

        {/* Tier 2 */}
        <View style={[styles.tierSectionHeader, { marginTop: 18 }]}>
          <View style={[styles.tierDot, { backgroundColor: '#f87171' }]} />
          <Text style={styles.tierSectionLabel}>Sharp Lines &amp; Exchanges</Text>
          <Text style={styles.tierSectionCount}>{ONBOARDING_BOOKS_T2.length} books</Text>
        </View>
        {renderBookGrid(ONBOARDING_BOOKS_T2)}
      </ScrollView>
    </View>
  );

  // ── Step 3: Preferences ───────────────────────────────────────────────────────
  const renderPreferences = () => (
    <View style={styles.stepContent}>
      <View style={styles.stepHeader}>
        <LinearGradient colors={['rgba(96,165,250,0.2)', 'rgba(59,130,246,0.1)']} style={styles.stepIconCircle}>
          <Ionicons name="options" size={26} color="#60a5fa" />
        </LinearGradient>
        <Text style={styles.stepTitle}>Your Settings</Text>
        <Text style={styles.stepSub}>Fine-tune how we display bets and calculate returns.</Text>
      </View>

      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 20 }}>
        <View style={styles.prefSection}>
          <Text style={styles.prefLabel}>Default Unit Size</Text>
          <Text style={styles.prefHint}>Your standard bet amount — used in Kelly sizing</Text>
          <View style={styles.chipRow}>
            {activeUnitSizes.map(unit => (
              <TouchableOpacity key={unit} style={[styles.chip, unitSize === unit && styles.chipActive]} onPress={() => setUnitSize(unit)}>
                <Text style={[styles.chipText, unitSize === unit && styles.chipTextActive]}>${unit}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TextInput
            style={styles.customInput}
            value={`$${unitSize}`}
            onChangeText={t => setUnitSize(t.replace(/[^0-9]/g, ''))}
            placeholder="Custom amount"
            placeholderTextColor="rgba(255,255,255,0.3)"
            keyboardType="numeric"
          />
        </View>

        <View style={styles.prefSection}>
          <Text style={styles.prefLabel}>Minimum EV% Filter</Text>
          <Text style={styles.prefHint}>Hide bets below this threshold (0% shows everything)</Text>
          <View style={styles.chipRow}>
            {['0', '1', '2', '3', '5'].map(ev => (
              <TouchableOpacity key={ev} style={[styles.chip, evThreshold === ev && styles.chipActiveGreen]} onPress={() => setEvThreshold(ev)}>
                <Text style={[styles.chipText, evThreshold === ev && { color: '#4ade80' }]}>{ev}%</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {bettingProfile && (
          <View style={styles.profileSummaryRow}>
            <Ionicons name="person-circle-outline" size={16} color="rgba(255,255,255,0.35)" />
            <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginLeft: 6 }}>
              Profile: {BETTING_PROFILES.find(p => p.id === bettingProfile)?.label}
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );

  // ── Step 4: Sports ────────────────────────────────────────────────────────────
  const renderSports = () => (
    <View style={styles.stepContent}>
      <View style={styles.stepHeader}>
        <LinearGradient colors={['rgba(251,191,36,0.2)', 'rgba(234,179,8,0.1)']} style={styles.stepIconCircle}>
          <Ionicons name="trophy" size={26} color="#fbbf24" />
        </LinearGradient>
        <Text style={styles.stepTitle}>Favorite Sports</Text>
        <Text style={styles.stepSub}>We'll surface the best bets for the sports you care about.</Text>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.sportsGrid} showsVerticalScrollIndicator={false}>
        {activeSports.map(sport => {
          const sel = selectedSports.includes(sport.id);
          return (
            <TouchableOpacity key={sport.id} style={[styles.sportCard, sel && styles.sportCardSel]} onPress={() => toggleSport(sport.id)} activeOpacity={0.7}>
              {sel && <View style={styles.sportCheck}><Ionicons name="checkmark-circle" size={16} color="#10B981" /></View>}
              <Text style={styles.sportEmoji}>{sport.icon}</Text>
              <Text style={[styles.sportName, sel && { color: '#fff' }]}>{sport.name}</Text>
              <Text style={styles.sportLeague}>{sport.league}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  // ── Step 5: Complete ──────────────────────────────────────────────────────────
  const renderComplete = () => {
    const profile = BETTING_PROFILES.find(p => p.id === bettingProfile);
    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.completeScroll} showsVerticalScrollIndicator={false}>
        {/* Success animation stand-in */}
        <View style={styles.completeIconWrap}>
          <View style={styles.completeGlowRing} />
          <LinearGradient colors={['#10B981', '#059669']} style={styles.completeCircle}>
            <Ionicons name="checkmark" size={48} color="#fff" />
          </LinearGradient>
        </View>

        <Text style={styles.stepTitle}>You're all set{tierCfg.badge ? ', tester' : ''}!</Text>
        <Text style={styles.stepSub}>Your preferences are saved. Dive in and start finding edges.</Text>

        {/* Summary card */}
        <View style={styles.summaryCard}>
          <GlassBackgroundGradient borderRadius={18} />
          <Text style={styles.summaryTitle}>Your Setup</Text>

          {[
            { icon: 'person-outline',   label: 'Profile',    value: profile?.label || '—', color: profile?.accent || '#a78bfa' },
            { icon: 'bookmark-outline', label: 'Books',      value: selectedBooks.length > 0 ? `${selectedBooks.length} sportsbook${selectedBooks.length > 1 ? 's' : ''}` : 'None yet', color: '#60a5fa' },
            { icon: 'cash-outline',     label: 'Unit Size',  value: `$${unitSize}`, color: '#4ade80' },
            { icon: 'trending-up',      label: 'Min EV',     value: `${evThreshold}%`, color: '#fbbf24' },
            { icon: 'trophy-outline',   label: 'Sports',     value: selectedSports.length > 0 ? `${selectedSports.length} selected` : 'All sports', color: '#f472b6' },
          ].map((row, i) => (
            <View key={i} style={styles.summaryRow}>
              <Ionicons name={row.icon} size={15} color={row.color} />
              <Text style={styles.summaryLabel}>{row.label}</Text>
              <Text style={[styles.summaryValue, { color: row.color }]}>{row.value}</Text>
            </View>
          ))}
        </View>

        {tierCfg.badge && (
          <View style={[styles.tierBadge, { backgroundColor: TIER_CONFIG[userTier].badgeBg, borderColor: TIER_CONFIG[userTier].badgeBorder, alignSelf: 'center', marginTop: 16 }]}>
            <Ionicons name={userTier === 'alpha' ? 'flask-outline' : 'rocket-outline'} size={12} color={TIER_CONFIG[userTier].badgeText} style={{ marginRight: 4 }} />
            <Text style={[styles.tierBadgeText, { color: TIER_CONFIG[userTier].badgeText }]}>
              {userTier === 'alpha' ? 'Alpha tester — your feedback matters' : 'Beta tester — updates drop weekly'}
            </Text>
          </View>
        )}
      </ScrollView>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0: return renderWelcome();
      case 1: return renderProfile();
      case 2: return renderMyBooks();
      case 3: return renderPreferences();
      case 4: return renderSports();
      case 5: return renderComplete();
      default: return null;
    }
  };

  const canContinue = () => {
    if (currentStep === 1 && !bettingProfile) return false;
    return true;
  };

  const STEP_LABELS = ['Welcome', 'Profile', 'Books', 'Settings', 'Sports', 'Done'];

  return (
    <View style={styles.container}>
      {/* Brand gradient — matches app-wide background */}
      <LinearGradient
        colors={['#000000', '#140d1f', '#291B32']}
        style={StyleSheet.absoluteFill}
        start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}
      />

      {/* Header */}
      <View style={styles.header}>
        {renderProgress()}
        {currentStep < totalSteps - 1 && !isPreview && (
          <TouchableOpacity style={styles.skipBtn} onPress={onSkip}>
            <Text style={styles.skipBtnText}>Skip</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Step label */}
      <View style={styles.stepLabelRow}>
        <Text style={styles.stepLabelText}>{STEP_LABELS[currentStep]}</Text>
      </View>

      {/* Content */}
      <Animated.View style={[styles.contentWrap, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
        {renderStep()}
      </Animated.View>

      {/* Navigation */}
      <View style={styles.navRow}>
        {currentStep > 0
          ? <TouchableOpacity style={styles.backBtn} onPress={prevStep}>
              <Ionicons name="arrow-back" size={18} color="rgba(255,255,255,0.6)" />
              <Text style={styles.backBtnText}>Back</Text>
            </TouchableOpacity>
          : <View />}

        <TouchableOpacity
          style={[styles.nextBtn, !canContinue() && styles.nextBtnDisabled]}
          onPress={canContinue() ? nextStep : undefined}
          activeOpacity={canContinue() ? 0.85 : 1}
        >
          <LinearGradient
            colors={canContinue() ? ['#8B5CF6', '#6366F1'] : ['rgba(255,255,255,0.08)', 'rgba(255,255,255,0.06)']}
            style={styles.nextBtnGrad}
            start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
          >
            <Text style={[styles.nextBtnText, !canContinue() && { color: 'rgba(255,255,255,0.3)' }]}>
              {currentStep === totalSteps - 1 ? "Let's Go!" : currentStep === 0 ? 'Get Started' : 'Continue'}
            </Text>
            {currentStep < totalSteps - 1 && canContinue() && (
              <Ionicons name="arrow-forward" size={17} color="#fff" />
            )}
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default OnboardingFlow;

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#000000' },

  // Header & progress
  header: {
    paddingTop: Platform.OS === 'web' ? 36 : 56,
    paddingHorizontal: 24,
    paddingBottom: 8,
    position: 'relative',
  },
  progressWrap: { alignItems: 'center' },
  progressTrack: {
    flexDirection: 'row',
    gap: 4,
    height: 4,
    width: '80%',
    maxWidth: 320,
  },
  progressSeg: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  progressSegActive: {
    backgroundColor: '#8B5CF6',
  },
  progressSegDone: {
    backgroundColor: '#10B981',
  },
  progressLabel: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    marginTop: 6,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  previewBadge: {
    alignSelf: 'center',
    backgroundColor: 'rgba(249,115,22,0.15)',
    paddingHorizontal: 12, paddingVertical: 4,
    borderRadius: 10,
    borderWidth: 1, borderColor: 'rgba(249,115,22,0.4)',
    marginBottom: 8,
  },
  previewBadgeText: { color: '#F97316', fontSize: 10, fontWeight: '700', letterSpacing: 1 },
  skipBtn: { position: 'absolute', right: 24, top: Platform.OS === 'web' ? 40 : 60 },
  skipBtnText: { color: 'rgba(255,255,255,0.35)', fontSize: 13, fontWeight: '500' },

  // Step label
  stepLabelRow: { alignItems: 'center', paddingBottom: 4 },
  stepLabelText: { color: 'rgba(255,255,255,0.2)', fontSize: 10, fontWeight: '700', letterSpacing: 1.5, textTransform: 'uppercase' },

  // Content
  contentWrap: { flex: 1, paddingHorizontal: 24 },

  // Navigation
  navRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
    paddingBottom: Platform.OS === 'web' ? 28 : 36,
  },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, padding: 8 },
  backBtnText: { color: 'rgba(255,255,255,0.5)', fontSize: 14 },
  nextBtn: { flex: 1, maxWidth: 240, marginLeft: 16, borderRadius: 14, overflow: 'hidden' },
  nextBtnDisabled: { opacity: 0.4 },
  nextBtnGrad: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 16, borderRadius: 14 },
  nextBtnText: { color: 'rgba(255,255,255,0.9)', fontSize: 16, fontWeight: '700', letterSpacing: 0.3 },

  // Step shared
  stepContent: { flex: 1 },
  stepHeader: { alignItems: 'center', marginBottom: 20 },
  stepIconCircle: {
    width: 60, height: 60, borderRadius: 18,
    justifyContent: 'center', alignItems: 'center', marginBottom: 12,
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.2)',
    borderLeftColor: 'rgba(255,255,255,0.14)',
    borderRightColor: 'rgba(255,255,255,0.08)',
    borderBottomColor: 'rgba(255,255,255,0.05)',
    overflow: 'hidden',
  },
  stepTitle: { color: '#fff', fontSize: 26, fontWeight: '800', textAlign: 'center', marginBottom: 8, lineHeight: 32 },
  stepSub: { color: 'rgba(255,255,255,0.5)', fontSize: 14, textAlign: 'center', lineHeight: 21, maxWidth: 380 },

  // ── Welcome ────────────────────────────────────────────────────────────────
  welcomeScroll: { alignItems: 'center', paddingBottom: 20, paddingTop: 8 },

  tierBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    paddingHorizontal: 14, paddingVertical: 6,
    borderRadius: 20, borderWidth: 1,
    marginBottom: 20,
  },
  tierBadgeDot: { width: 6, height: 6, borderRadius: 3 },
  tierBadgeText: { fontSize: 11, fontWeight: '800', letterSpacing: 1 },

  logoWrap: { alignItems: 'center', justifyContent: 'center', marginBottom: 4 },
  logoGlow: {
    position: 'absolute',
    width: 140, height: 140,
    borderRadius: 70,
    opacity: 0.6,
  },
  logoGradient: {
    width: 96, height: 96, borderRadius: 24,
    justifyContent: 'center', alignItems: 'center',
    shadowColor: '#8B5CF6', shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.6, shadowRadius: 20, elevation: 12,
  },
  logoText: { color: '#fff', fontSize: 42, fontWeight: '900' },

  welcomeHeadline: {
    color: '#fff', fontSize: 30, fontWeight: '900',
    textAlign: 'center', marginTop: 18, marginBottom: 10,
    lineHeight: 36,
  },
  welcomeSub: {
    color: 'rgba(255,255,255,0.5)', fontSize: 15,
    textAlign: 'center', lineHeight: 22,
    maxWidth: 360, marginBottom: 24,
  },

  statPills: { flexDirection: 'row', gap: 10, marginBottom: 24 },
  statPill: {
    flex: 1, alignItems: 'center', gap: 4,
    position: 'relative', overflow: 'hidden',
    borderRadius: 12, paddingVertical: 14, paddingHorizontal: 8,
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.18)',
    borderLeftColor: 'rgba(255,255,255,0.14)',
    borderRightColor: 'rgba(255,255,255,0.08)',
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  statPillValue: { fontSize: 17, fontWeight: '800' },
  statPillLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: '600', textAlign: 'center' },

  featureGrid: { width: '100%', gap: 10 },
  featureCard: {
    borderRadius: 14, overflow: 'hidden', position: 'relative',
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.2)',
    borderLeftColor: 'rgba(255,255,255,0.14)',
    borderRightColor: 'rgba(255,255,255,0.08)',
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  featureCardBlur: { flexDirection: 'row', alignItems: 'center', padding: 16, gap: 14 },
  featureCardIcon: {
    width: 42, height: 42, borderRadius: 12,
    backgroundColor: 'rgba(162,89,255,0.12)',
    borderWidth: 1, borderColor: 'rgba(162,89,255,0.25)',
    justifyContent: 'center', alignItems: 'center',
    flexShrink: 0,
  },
  featureCardTitle: { color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: '700', marginBottom: 3 },
  featureCardDesc: { color: 'rgba(255,255,255,0.45)', fontSize: 12, lineHeight: 18 },

  // ── Profile ────────────────────────────────────────────────────────────────
  profileRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 24,
    marginBottom: 14,
  },
  profileCardWrap: { flex: 1 },
  profileCard: {
    flex: 1,
    position: 'relative',
    borderRadius: 20,
    overflow: 'hidden',
    // Default differential border (Plans page style)
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.22)',
    borderLeftColor: 'rgba(255,255,255,0.16)',
    borderRightColor: 'rgba(255,255,255,0.10)',
    borderBottomColor: 'rgba(255,255,255,0.06)',
    minHeight: 185,
  },
  profileCardBlur: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
    paddingVertical: 22, paddingHorizontal: 14,
    gap: 10,
  },
  profileAccentBar: {
    position: 'absolute', top: 0, left: 0, right: 0,
    height: 2,
  },
  profileIconCircle: {
    width: 50, height: 50, borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
    justifyContent: 'center', alignItems: 'center',
  },
  profileLabel: { color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: '800', textAlign: 'center' },
  profileTag: {
    paddingHorizontal: 7, paddingVertical: 3,
    borderRadius: 6, borderWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
  },
  profileTagText: { color: 'rgba(255,255,255,0.3)', fontSize: 8, fontWeight: '800', letterSpacing: 0.8, textAlign: 'center' },
  profileTagline: { color: 'rgba(255,255,255,0.4)', fontSize: 11, textAlign: 'center' },
  profileDescPanel: {
    flexDirection: 'row', alignItems: 'flex-start',
    borderRadius: 14, borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.15)',
    borderLeftColor: 'rgba(255,255,255,0.10)',
    borderRightColor: 'rgba(255,255,255,0.06)',
    borderBottomColor: 'rgba(255,255,255,0.04)',
    padding: 14, marginTop: 4, overflow: 'hidden',
  },
  profileDescText: { flex: 1, fontSize: 13, lineHeight: 19 },

  // ── Books ──────────────────────────────────────────────────────────────────
  tierSectionHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginBottom: 10,
  },
  tierDot: { width: 8, height: 8, borderRadius: 4 },
  tierSectionLabel: { color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: '700', flex: 1 },
  tierSectionCount: { color: 'rgba(255,255,255,0.3)', fontSize: 11 },
  bookCountRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 14,
  },
  bookCountText: { color: '#a78bfa', fontSize: 13, fontWeight: '700' },
  bookClearText: { color: 'rgba(255,255,255,0.35)', fontSize: 12 },
  booksGrid: {
    flexDirection: 'row', flexWrap: 'wrap',
    gap: 8,
  },
  bookCard: {
    width: 86, position: 'relative',
    borderRadius: 14, paddingVertical: 12, paddingHorizontal: 6,
    overflow: 'hidden',
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.16)',
    borderLeftColor: 'rgba(255,255,255,0.12)',
    borderRightColor: 'rgba(255,255,255,0.07)',
    borderBottomColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
  },
  bookCardSel: {
    borderTopColor: 'rgba(16,185,129,0.7)',
    borderLeftColor: 'rgba(16,185,129,0.5)',
    borderRightColor: 'rgba(16,185,129,0.3)',
    borderBottomColor: 'rgba(16,185,129,0.15)',
  },
  bookCheck: { position: 'absolute', top: 5, right: 5, zIndex: 2 },
  bookLogoWrap: {
    width: 46, height: 46, borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.06)',
    justifyContent: 'center', alignItems: 'center',
    marginBottom: 8, overflow: 'hidden',
  },
  bookLogo: { width: 42, height: 42, borderRadius: 9 },
  bookLogoFallbackText: { color: 'rgba(255,255,255,0.55)', fontSize: 18, fontWeight: '800' },
  bookName: { color: 'rgba(255,255,255,0.6)', fontSize: 9.5, fontWeight: '600', textAlign: 'center', lineHeight: 13 },

  // ── Prefs ──────────────────────────────────────────────────────────────────
  prefSection: { marginBottom: 24 },
  prefLabel: { color: '#fff', fontSize: 15, fontWeight: '700', marginBottom: 4 },
  prefHint: { color: 'rgba(255,255,255,0.35)', fontSize: 12, marginBottom: 12 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
  },
  chipActive: { backgroundColor: 'rgba(139,92,246,0.2)', borderColor: 'rgba(139,92,246,0.5)' },
  chipActiveGreen: { backgroundColor: 'rgba(74,222,128,0.15)', borderColor: 'rgba(74,222,128,0.4)' },
  chipText: { color: 'rgba(255,255,255,0.65)', fontSize: 14, fontWeight: '600' },
  chipTextActive: { color: '#fff' },
  customInput: {
    marginTop: 12, padding: 13, borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
    color: '#fff', fontSize: 14,
  },
  profileSummaryRow: {
    flexDirection: 'row', alignItems: 'center',
    marginTop: 8, padding: 12, borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)',
  },

  // ── Sports ─────────────────────────────────────────────────────────────────
  sportsGrid: {
    flexDirection: 'row', flexWrap: 'wrap',
    justifyContent: 'center', gap: 10, paddingBottom: 20,
  },
  sportCard: {
    width: 95, padding: 14, borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
  },
  sportCardSel: { backgroundColor: 'rgba(139,92,246,0.15)', borderColor: 'rgba(139,92,246,0.4)' },
  sportCheck: { position: 'absolute', top: 5, right: 5 },
  sportEmoji: { fontSize: 30, marginBottom: 8 },
  sportName: { color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: '700' },
  sportLeague: { color: 'rgba(255,255,255,0.35)', fontSize: 9, marginTop: 2, textAlign: 'center' },

  // ── Complete ───────────────────────────────────────────────────────────────
  completeScroll: { alignItems: 'center', paddingBottom: 20 },
  completeIconWrap: { alignItems: 'center', justifyContent: 'center', marginVertical: 24 },
  completeGlowRing: {
    position: 'absolute', width: 130, height: 130,
    borderRadius: 65, backgroundColor: 'rgba(16,185,129,0.15)',
  },
  completeCircle: {
    width: 96, height: 96, borderRadius: 48,
    justifyContent: 'center', alignItems: 'center',
    shadowColor: '#10B981', shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.5, shadowRadius: 20, elevation: 10,
  },
  summaryCard: {
    width: '100%', borderRadius: 18, padding: 20, marginTop: 20,
    overflow: 'hidden',
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.2)',
    borderLeftColor: 'rgba(255,255,255,0.14)',
    borderRightColor: 'rgba(255,255,255,0.08)',
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  summaryTitle: { color: '#fff', fontSize: 15, fontWeight: '800', marginBottom: 14 },
  summaryRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  summaryLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 13, flex: 1 },
  summaryValue: { fontSize: 13, fontWeight: '700' },
});
